import dayjs from "dayjs";
import { FormInstance } from "antd";

import {
  IFollowerInterface,
  IDefaultValue,
  IMillValue,
  IMillWatch,
  IMillDetail,
  TTransaction,
  IProductGroupInterface,
  ITransactionPreview,
} from "@components/Form/interface";
import {
  IConfigFormTransaction,
  IMasterMethod,
  IMasterOrigin,
  TConfigEditFormTransaction,
} from "@zustand/interface";
import {
  checkCondition,
  checkEmptyArray,
  checkEmptyNumber,
  checkEmptyString,
  checkEmptyUndefined,
} from "@utils/checkEmpty";
import { IMostShipping } from "@components/Barcode/interface";
import { IOptions, IVenderDetail } from "@interface/ICommon";
import { handleValueArray, handleValueOr } from "./manageValue";
import { dateFormatBookingDate, timeFormatResponse } from "./time";
import {
  IResponseSearchTruckAPI,
  TValuesFormRegisterDriver,
  TValuesFormSellRecall,
} from "@interface/ITranspot";
import { TEditData } from "@interface/IManageData";

export const checkBookingDetail = (
  values: IDefaultValue,
  configForm?: IConfigFormTransaction,
) => {
  return values?.check_booking
    ? {
        booking_detail: {
          booking_date: values?.plan_time,
          unloading_location: values?.plan_location ?? "",
          master_method_code: checkCondition(
            Boolean(configForm?.method?.display),
            values.method,
            undefined,
          ),
        },
      }
    : {};
};

const buildSurveySection = (
  key: string | undefined,
  sectionName: "sanitation" | "fumigation",
  values: IDefaultValue,
) => {
  return checkCondition(
    key,
    {
      [sectionName]: {
        master_questions_code: Number.parseInt(key?.split("_")?.[1] ?? "0"),
        master_choices_code: values?.[key as keyof IDefaultValue],
      },
    },
    {},
  );
};

export const checkOptionMethodData = (
  values: IDefaultValue,
  configForm?: IConfigFormTransaction,
  mill: IMillValue[] = [],
  surveyKey?: string[],
) => {
  const methodBody = checkCondition(
    Boolean(configForm?.method?.display),
    {
      truck_information_method: {
        master_method_information_code: values.method,
        mill_id: mill[0]?.mill_id,
        number: +(values.method_quantity ?? 0),
      },
    },
    {},
  );

  const originBody = checkCondition(
    Boolean(configForm?.origin?.display),
    { master_origin_information_code: values.origin },
    {},
  );

  const mostRecentBody = checkCondition(
    Boolean(configForm?.most_recent_shipment?.display),
    {
      most_recent_shipment: values.most_shipping?.map(
        (e: string, index: number) => {
          return {
            order: index + 1,
            value: e,
          };
        },
      ),
    },
    {},
  );

  const surveyBody = checkCondition(
    Boolean(configForm?.most_recent_shipment?.display),
    () => {
      const surveySections = ["sanitation", "fumigation"] as const;
      const surveySectionData = surveySections.reduce((acc, section, index) => {
        return {
          ...acc,
          ...buildSurveySection(surveyKey?.[index], section, values),
        };
      }, {});
      return {
        surveys: {
          master_surveys_code: values?.master_surveys_code,
          most_recent_shipment: values.most_shipping?.map(
            (e: string, index: number) => {
              const nameChoicesCode =
                `master_choices_code_${e}_${index}` as keyof IDefaultValue;
              const nameQuestionsCode =
                `master_questions_code_${e}_${index}` as keyof IDefaultValue;
              const nameExtra = `extra_${e}_${index}` as keyof IDefaultValue;
              return {
                order: index + 1,
                value: e,
                master_choices_code: values?.[nameChoicesCode],
                master_questions_code: values?.[nameQuestionsCode],
                shipment_value: values?.[nameExtra],
              };
            },
          ),
          ...surveySectionData,
        },
      };
    },
    () => {},
  );

  return { methodBody, originBody, mostRecentBody, surveyBody };
};

export const deliveryVehiclesDTO = (values: IDefaultValue) => {
  const follower = formatFollower(values?.follower ?? []);
  return {
    class_code: values.vehicle_type_code,
    master_class_id: values.vehicle_type,
    front_plate_no: values.vehicle_registration,
    front_plate_province: checkEmptyString(
      values.vehicle_registration_province,
    ),
    back_plate_no: checkEmptyString(values?.trailer_registration),
    back_plate_province: checkEmptyString(
      values?.trailer_registration_province,
    ),
    driver_detail: {
      first_name: checkEmptyString(values.driver_name),
      last_name: checkEmptyString(values.driver_surname),
      personal_id: values.driver_id,
      tel: values.driver_phone,
      temperature: checkCondition(
        values.driver_temp,
        Number.parseFloat(checkEmptyString(values.driver_temp?.toString())),
        undefined,
      ),
    },
    follwer_details: follower,
  };
};

export const editTransactionPatchDTO = (
  corporationCode: string,
  values: IDefaultValue,
  configForm?: IConfigFormTransaction,
  questionValue?: string[],
  configEditForm?: TConfigEditFormTransaction,
) => {
  const editValue = <T, F>(key: string, value: T, emptyValue: F) => {
    return checkCondition(
      !configEditForm?.is_enable ||
        configEditForm.config_key.some(({ fe_key }) => fe_key === key),
      value ?? emptyValue,
      undefined,
    );
  };

  const millRaw = checkEmptyArray(
    checkEmptyArray(values?.mill)?.map((val: IMillWatch) => {
      if (val.mill_data) {
        const millDetail = JSON.parse(
          checkCondition(val.mill_data, val.mill_data, "{}"),
        );
        return {
          mill_id: checkCondition(
            Number.parseInt(millDetail.id),
            Number.parseInt(millDetail.id),
            0,
          ),
          weight: Number.parseInt(val.mill_weight as string),
        };
      }
    }),
  );
  millRaw.pop();
  const mill = millRaw as IMillValue[];

  const { mostRecentBody, surveyBody } = checkOptionMethodData(
    values,
    configForm,
    mill,
    questionValue,
  );
  const vehicle = deliveryVehiclesDTO(values);
  const surveys = surveyBody();

  return {
    corporation_code: corporationCode,
    delivery_vehicles: [
      {
        class_code: checkCondition(vehicle.class_code, vehicle.class_code, ""),
        master_class_id: checkCondition(
          vehicle.master_class_id,
          vehicle.master_class_id,
          0,
        ),
        front_plate_no: editValue(
          "vehicle_registration",
          vehicle.front_plate_no,
          "",
        ),
        front_plate_province: editValue(
          "vehicle_registration_province",
          vehicle.front_plate_province,
          "",
        ),
        back_plate_no: editValue(
          "trailer_registration",
          vehicle.back_plate_no,
          "",
        ),
        back_plate_province: editValue(
          "trailer_registration_province",
          vehicle.back_plate_province,
          "",
        ),
        driver_detail: {
          first_name: editValue(
            "driver_name",
            vehicle.driver_detail.first_name,
            "",
          ),
          last_name: editValue(
            "driver_surname",
            vehicle.driver_detail.last_name,
            "",
          ),
          personal_id: editValue(
            "driver_id",
            vehicle.driver_detail.personal_id,
            "",
          ),
          tel: editValue("driver_phone", vehicle.driver_detail.tel, ""),
          temperature: editValue(
            "driver_temp",
            vehicle.driver_detail.temperature,
            0,
          ),
        },
        follwer_details: editValue("follower", vehicle.follwer_details, []),
      },
    ],
    mill_details: editValue("mill", mill, []),
    split_contact: editValue(
      "check_split_contact",
      !!values?.check_split_contact,
      false,
    ),
    lot_no: editValue("lot_no", values?.lot_no, ""),
    most_recent_shipment: editValue(
      "most_recent_shipment",
      "most_recent_shipment" in mostRecentBody
        ? mostRecentBody.most_recent_shipment
        : undefined,
      [],
    ),
    surveys: editValue("most_recent_shipment", surveys?.surveys, ""),
  };
};

export const editPreviewDataDTO = (
  corporationCode: string,
  values: IDefaultValue,
): TEditData => {
  return {
    corporation_code: corporationCode,
    delivery_vehicles: [deliveryVehiclesDTO(values)],
  };
};

export const cancelTransactionDTO = (
  data: TTransaction,
  millWatch: IMillWatch[],
) => {
  const { booking_date, booking_time, unloading_location } =
    data?.booking_record && data?.booking_record.length > 0
      ? data.booking_record[0]
      : { booking_date: "", booking_time: "", unloading_location: "" };

  return {
    id: data?.id,
    plant_code: checkEmptyString(data?.factory_code),
    transaction_no: checkEmptyString(data?.code),
    bookingdate: booking_date ? dayjs(booking_date).format("DD-MM-YYYY") : "",
    bookingtime: checkEmptyString(booking_time),
    location: checkEmptyString(unloading_location),
    productgroup: checkEmptyString(data?.product_code),
    vendor_code: checkEmptyString(data?.vendor_code),
    producer_code: millWatch
      ? JSON.parse(millWatch[0]?.mill_data ?? "{}").mill_code
      : "",
    truck: [
      {
        car_plate_no: `${data?.truck_detail?.car_plate_no}`,
        trailer_plate_no: data?.trailer_detail
          ? `${data?.trailer_detail?.car_plate_no}`
          : "",
        truck_class_code: data?.truck_class,
      },
    ],
    cancel_status: "Y",
  };
};

const getMethod = (data: TTransaction) => {
  return data?.truck_information_method?.length
    ? data?.truck_information_method[0]?.master_method_information_code
    : 1;
};

const getMethodQuantity = (data: TTransaction) => {
  return data?.truck_information_method?.length &&
    data?.truck_information_method[0]?.number !== 0
    ? data?.truck_information_method[0]?.number
    : undefined;
};

const getMostShipping = (data: TTransaction): string[] => {
  return handleValueArray(
    data?.most_recent_shipment?.map((value: IMostShipping) => value.value),
  );
};

export const getVenderDetail = (data: TTransaction): string | number => {
  return data?.truck_detail?.truck_class_id
    ? handleValueOr(data?.truck_detail?.truck_class_id, "")
    : handleValueOr(data?.trailer_detail?.trailer_class_id as number, "");
};

export const manageDefaultValueDTO = (
  data: TTransaction,
  millData: IMillDetail[],
  form: FormInstance,
) => {
  const value: IDefaultValue = {
    vendor_code: checkEmptyString(data?.vendor_code),
    product_code: checkEmptyString(data?.product_code),
    factory_code: checkEmptyString(data?.factory_code),
    vehicle_type_code: checkEmptyString(data?.truck_class),
    vehicle_type: getVenderDetail(data),
    vehicle_registration: checkEmptyString(data?.truck_detail?.car_plate_no),
    vehicle_registration_province: checkEmptyString(
      data?.truck_detail?.car_plate_province,
    ),
    driver_name: checkEmptyString(data?.driver_detail?.firstname),
    driver_surname: checkEmptyString(data?.driver_detail?.lastname),
    driver_id: checkEmptyString(`${data?.driver_detail?.personal_id}`),
    driver_phone: checkEmptyString(`${data?.driver_detail?.tel}`),
    driver_temp: checkEmptyString(data?.driver_detail?.temperature?.toString()),
    method: getMethod(data),
    method_quantity: getMethodQuantity(data),
    origin: data?.master_origin_information_code,
    most_shipping: getMostShipping(data),
    po_number: checkEmptyString(data?.po_number),
    move_factory_code: checkEmptyUndefined(data?.move_factory_code),
    check_destination_factory: checkCondition(
      data?.move_factory_code,
      true,
      undefined,
    ),
    check_split_contact: checkCondition(data?.split_contact, true, undefined),
    lot_no: checkCondition(data?.lot_no, data?.lot_no, ""),
  };
  if (getMostShipping(data)?.length === 3 && data?.surveys) {
    value.certify = true;
    value.commit_vehicle = true;
    value.master_surveys_code = data?.surveys?.master_surveys_code;

    // Map most recent shipment data
    getMostShipping(data).forEach((shipmentValue, index) => {
      const shipmentData = data?.surveys?.most_recent_shipment?.[index];
      if (shipmentData) {
        Object.assign(value, {
          [`choices_${shipmentValue}_${index}`]:
            shipmentData.master_choices_code,
          [`master_questions_code_${shipmentValue}_${index}`]:
            shipmentData.master_questions_code,
          [`master_choices_code_${shipmentValue}_${index}`]:
            shipmentData.master_choices_code,
          [`extra_${shipmentValue}_${index}`]: shipmentData.shipment_value,
          [`master_choices_name_${shipmentValue}_${index}`]:
            shipmentData.master_choices_name,
        });
      }
    });

    // Map sanitation and fumigation data
    const surveyTypes = [
      { key: "sanitation", data: data?.surveys.sanitation },
      { key: "fumigation", data: data?.surveys.fumigation },
    ];

    surveyTypes.forEach(({ data: surveyData }) => {
      if (surveyData?.master_questions_code) {
        Object.assign(value, {
          [`question_${surveyData.master_questions_code}`]:
            surveyData.master_choices?.map(
              (choice) => choice.master_choice_code,
            ),
        });
      }
    });
  }

  if (data?.trailer_detail?.car_plate_no) {
    value.trailer_registration = checkEmptyString(
      data?.trailer_detail?.car_plate_no,
    );
    value.trailer_registration_province =
      data?.trailer_detail?.car_plate_province;
  }
  if (!data?.millCode && data?.truck_information_mill_balance) {
    const mill: IMillWatch[] = [];
    data?.truck_information_mill_balance.map((val) => {
      mill.push({
        mill_data: JSON.stringify(millData?.find((e) => e.id === val.mill_id)),
        mill_weight: val.weight,
        submit: true,
      });
    });
    value.mill = [...mill, {}];
    form.setFieldsValue({
      mill: mill,
    });
  } else {
    value.mill_code_name = checkEmptyUndefined(data?.mill_name);
    value.original_weight = checkEmptyNumber(data?.car_weight);
  }
  if (data?.booking_record && data?.booking_record?.length > 0) {
    const { booking_date, booking_time, unloading_location } =
      data.booking_record[0];
    const time = timeFormatResponse(booking_time);

    value.check_booking = true;
    value.plan_date = dayjs(booking_date);
    value.plan_time = dateFormatBookingDate(booking_date, time);
    value.plan_time_preview = time;
    value.plan_location = unloading_location;
  }
  if (data?.follower_detail) {
    const follower: IFollowerInterface[] = [];
    data?.follower_detail.map((val) => {
      follower.push({
        name_follower: val.firstname,
        surname_follower: val.lastname,
        temp_follower: checkEmptyString(val?.temperature?.toString()),
      });
    });
    value.follower = follower;
  }
  return value;
};

export const setDataTransactionDTO = (
  form: FormInstance,
  vendorCode: string,
  productGroupData: IProductGroupInterface[],
  configForm: IConfigFormTransaction | undefined,
  corporation_code: string,
  questionValue: string[],
  detailVendor?: IVenderDetail,
) => {
  const values = form.getFieldsValue();
  let mill_name = "";
  const mill: IMillValue[] =
    (values?.mill ?? [])?.map((val: IMillWatch) => {
      if (val.mill_data) {
        const millDetail = JSON.parse(val.mill_data ?? "{}");
        mill_name = millDetail?.mill_name;
        return {
          mill_id: Number.parseInt(millDetail.id) ?? 0,
          weight: Number.parseInt(val.mill_weight as string),
        };
      }
    }) ?? [];
  mill.pop();

  const delivery_vehicles = deliveryVehiclesDTO(values);

  const bookingDetail = checkBookingDetail(values, configForm);

  const { methodBody, originBody, mostRecentBody, surveyBody } =
    checkOptionMethodData(values, configForm, mill, questionValue);

  return {
    lot_no: values?.lot_no,
    split_contact: values?.check_split_contact,
    create_by_corporation: corporation_code,
    factory_code: values.factory_code,
    product_code: values.product_code,
    benefit_code: "",
    supplier_type_code: "",
    mill_mame: mill_name,
    weight: checkEmptyNumber(values.original_weight),
    vendor_detail: {
      vendor_code: values.vendor_code ?? vendorCode,
      vendor_tel: checkEmptyString(detailVendor?.telno),
    },
    delivery_vehicles: [delivery_vehicles],
    mill_details: mill,
    po_number: values.po_number,
    product_name: checkEmptyString(
      productGroupData?.find(
        (val: IProductGroupInterface) =>
          val.productGroupCode === values.product_code,
      )?.productGroupName,
    ),
    move_factory_code: checkEmptyUndefined(values.move_factory_code),
    ...bookingDetail,
    ...methodBody,
    ...originBody,
    ...mostRecentBody,
    ...surveyBody(),
  };
};

export const manageTransactionDetailDTO = (data: ITransactionPreview) => ({
  ...data,
  driver_detail: {
    ...data.driver_detail,
    temperature: checkCondition(
      data.driver_detail.temperature === 0,
      undefined,
      data.driver_detail.temperature,
    ),
  },
  follower_detail: data.follower_detail.map((follower) => ({
    ...follower,
    temperature: checkCondition(
      follower.temperature === 0,
      undefined,
      follower.temperature,
    ),
  })),
});

export const getMillName = (master: IMasterMethod[], method: number) => {
  return master?.find((val: IMasterMethod) => val.master_method_code === method)
    ?.name;
};

export const getNameValues = (master: IOptions[], values: number | string) => {
  return master?.find(
    (val: IOptions) => val.value?.toString() === values?.toString(),
  )?.label;
};

const parseOptionalTemperature = (value?: string) => {
  const temperature = Number.parseFloat(checkEmptyString(value));
  return checkCondition(Number.isFinite(temperature), temperature, undefined);
};

export const registerDriverBodyDTO = (values: TValuesFormRegisterDriver) => {
  const follower = values?.follower?.map((val) => {
    return {
      firstname: val.name_follower,
      lastname: checkEmptyString(val.surname_follower),
      temperature: parseOptionalTemperature(val.temp_follower),
    };
  });
  return {
    firstname: values?.driver_name,
    lastname: values?.driver_surname,
    personal_id: values?.driver_id,
    tel: values?.driver_phone,
    truck_class: values?.vehicle_type_code,
    master_class_id: values?.vehicle_type,
    front_plate_province: values?.vehicle_registration_province,
    front_plate_no: values?.vehicle_registration,
    back_plate_province: values?.trailer_registration_province,
    back_plate_no: values?.trailer_registration,
    followers: follower,
  };
};

const formatFollower = (follower: IFollowerInterface[]) => {
  return follower?.map((val) => {
    return {
      first_name: val.name_follower,
      last_name: checkCondition(
        val.surname_follower,
        val.surname_follower,
        undefined,
      ),
      temperature: parseOptionalTemperature(val.temp_follower),
    };
  });
};

export const sellRecordBodyDTO = (values: TValuesFormSellRecall) => {
  return {
    truck_class: values?.vehicle_type_code,
    master_class_id: values?.vehicle_type,
    front_plate_province: values?.vehicle_registration_province,
    front_plate_no: values?.vehicle_registration,
    back_plate_province: values?.trailer_registration_province,
    back_plate_no: values?.trailer_registration,
  };
};

export const initialSearchTruckDTO = (data: IResponseSearchTruckAPI) => {
  return {
    vehicle_type_code: data?.truck_class,
    vehicle_type_name: data?.truck_class,
    vehicle_type: data?.master_class_id,
    vehicle_registration_province: data?.front_plate_province,
    vehicle_registration: data?.front_plate_no,
    trailer_registration_province: checkEmptyString(data?.back_plate_province),
    trailer_registration: checkEmptyString(data?.back_plate_no),
    driver_id: checkEmptyString(data?.personal_id),
    driver_name: checkEmptyString(data?.firstname),
    driver_surname: checkEmptyString(data?.lastname),
    driver_phone: checkEmptyString(data?.tel),
    follower: data?.followers?.map((val) => {
      return {
        name_follower: checkEmptyString(val.firstname),
        surname_follower: checkEmptyString(val.lastname),
        temp_follower: checkEmptyString(val?.temperature?.toString()),
      };
    }),
  };
};

export const masterOriginDto = <
  T extends Pick<IMasterOrigin, "name" | "language_key"> & {
    master_origin_information_code: number;
  },
>(
  message: T[],
): IMasterOrigin[] => {
  return message?.map((val: T) => {
    return {
      ...val,
      id: val.master_origin_information_code,
    };
  });
};
